import React, { useEffect, useCallback } from 'react';
import { hooks, media } from '@lifesize/clients.sdk';
import PreviewVideo from 'components/PreviewVideo/PreviewVideo';
import classes from './CallPreview.module.scss';
import { logger } from 'utils/logger';
import VideoButton from './VideoButton';
import AudioButton from './AudioButton';

export interface Props {
  defaultVideoSetting: boolean;
  defaultAudioSetting: boolean;
  isVideoMuted: boolean;
  isAudioMuted: boolean;
  onVideoMuteClick: () => void;
  onAudioMuteClick: () => void;
  inActiveCall: boolean;
}

const CallPreview = (props: Props) => {
  const { defaultVideoSetting, defaultAudioSetting } = props;
  const hasIncomingCalls = hooks.useIncomingCalls();

  // CA-2170: This must be a useCallback, otherwise hasIncomingCalls can desync
  // from parent component during this component's unmount
  const stopMedia = useCallback(() => {
    try {
      if (!hasIncomingCalls.length) {
        media.stopWebcam();
      }
    } catch (ex) {
      logger.info('Unable to release webcam', ex);
    }
  }, [hasIncomingCalls]);

  useEffect(() => {
    const setupMedia = async () => {
      try {
        if (!media.getPrimaryStream()) {
          // @ts-ignore
          await media.acquireWebcam(); // PT: TODO get default settings
          if (!defaultVideoSetting) { // this means video muted
            await media.setVideoMute(true);
          }
          if (!defaultAudioSetting) {
            await media.setAudioMute(true);
          }
        }
      } catch (ex) {
        logger.info('unable to capture stream in CallPreview', ex);
      }
    }

    // This will stop media if there are no incoming calls
    // We need to do this prior to invoking setupMedia()
    stopMedia();
    if (hasIncomingCalls.length) {
      setupMedia();
    }

    return stopMedia;
  }, [defaultVideoSetting, defaultAudioSetting, stopMedia, hasIncomingCalls]);

  return (
    <div className={classes.container}>
      <PreviewVideo />
      <div className={classes.buttonBar}>
        <VideoButton {...props} />
        <AudioButton {...props} />
      </div>
    </div>
  );
}

export default CallPreview;
